import SideBarContainer from 'Containers/SideBarContainer';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import { useHistory, useParams } from 'react-router-dom';
import useEmployeeCoursesReportPage from './hooks/useEmployeeCoursesReportPage';
import { Padding } from 'Components/Padding';
import GenericTable from './components/GenericTable/GenericTable';
import ProgressIndicator from 'Components/ProgressIndicator';
import EmployeeCoursesFilters from './components/EmployeeCoursesFilters/EmployeeCoursesFilters';
import TotalEmployeeDetails from './components/TotalEmployeeDetails/TotalEmployeeDetails';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CourseStatusChip from 'Components/CourseStatusChip';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { formatDate } from 'Utils/format-date';
import { buildTime } from 'Pages/reports/utils/buildTime';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import { EmployeeCoursesReportRowI } from 'types';

interface Params {
  employeeId: string;
}

export default function Page() {
  const { employeeId } = useParams<Params>();
  const history = useHistory();
  const {
    t2,
    employee,
    handleSearch,
    handleExport,
    coursesReportData,
    coursesReportError,
    coursesReportTotals,
    coursesReportLoading,
    handleDownloadCertificate,
  } = useEmployeeCoursesReportPage(employeeId);

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('employees_report'), path: '/reporte/empleados' },
            { label: t2('employee_detail'), path: `/reporte/empleados/${employeeId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${employee?.fullName}"`} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalEmployeeDetails totals={coursesReportTotals} />
        <EmployeeCoursesFilters onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <GenericTable<EmployeeCoursesReportRowI>
          columns={[
            { id: 'courseName', label: t2('course_name') },
            { id: 'coursePriority', label: t2('course_priority') },
            { id: 'category', label: t2('category') },
            { id: 'trainingHours', label: t2('training_hours') },
            { id: 'evaluationsProgress', label: t2('evaluations_progress'), align: 'center' },
            { id: 'courseStatus', label: t2('course_status'), align: 'center' },
            { id: 'evaluationDate', label: t2('evaluation_date'), align: 'center' },
            { id: 'downloadCertificate', label: t2('download_certificate'), align: 'center' },
          ]}
          rows={coursesReportData.map(userCourse => ({
            id: userCourse.id,
            courseId: userCourse.courseId,
            courseName: userCourse.courseName,
            coursePriority: userCourse.priorityName,
            category: userCourse.categoryName,
            trainingHours: buildTime(userCourse.trainingHours, t2),
            evaluationsProgress: (
              <ProgressIndicator
                completed={userCourse.completedEvaluations}
                total={userCourse.totalEvaluations}
                emptyMessage={t2('nothing_to_evaluate')}
              />
            ),
            courseStatus: <CourseStatusChip statusId={userCourse.userCourseStatusId} status={userCourse.statusName} />,
            evaluationDate: userCourse.evaluationDate ? (
              formatDate(userCourse.evaluationDate)
            ) : (
              <GenericTableText text={t2('not_evaluation_date')} tooltip={t2('not_evaluation_date_tooltip')} />
            ),
            downloadCertificate: (
              <CertificateDownload
                certificationId={userCourse.certificationId}
                onDownload={() =>
                  handleDownloadCertificate(userCourse.courseId, employeeId, userCourse.certificationId)
                }
              />
            ),
          }))}
          loading={coursesReportLoading}
          error={coursesReportError}
          onRowClick={row => {
            history.push(`/reporte/cursos/${row.courseId}`);
          }}
        />
      </Padding>
    </SideBarContainer>
  );
}
